import { useState, useEffect, useCallback } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { 
  Ticket, ShieldCheck, Plus, Copy, History, LogOut, Search,
  CheckCircle, XCircle, Loader2, Trash2, Users, Lock, ArrowRight,
  LayoutGrid, Mail, UserCheck, FileSpreadsheet, Key, Menu, X,
  FileText, RefreshCw, AlertCircle, BarChart3, ClipboardList, Settings
} from "lucide-react";
import { Toaster, toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Hook to remove Emergent badge
const useRemoveBadge = () => {
  useEffect(() => {
    const removeBadge = () => {
      const badge = document.getElementById('emergent-badge');
      if (badge) {
        badge.style.cssText = 'display:none!important;visibility:hidden!important;opacity:0!important;width:0!important;height:0!important;position:absolute!important;left:-9999px!important;';
      }
      document.querySelectorAll('a[href*="emergent.sh"]').forEach(el => {
        if (el.id !== 'root') {
          el.style.cssText = 'display:none!important;visibility:hidden!important;';
        }
      });
    };
    
    removeBadge();
    const interval = setInterval(removeBadge, 50);
    
    return () => clearInterval(interval);
  }, []);
};

// ============ PORTAL PAGE ============
const Portal = () => {
  const navigate = useNavigate();
  
  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      {/* Left Side - Branding */}
      <div className="lg:w-1/2 bg-gradient-to-br from-[#60A0FF] to-[#2B6BC0] p-8 lg:p-12 flex flex-col justify-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-40 h-40 border border-white rounded-full"></div>
          <div className="absolute bottom-20 right-10 w-60 h-60 border border-white rounded-full"></div>
        </div>
        <div className="relative z-10 animate-in fade-in slide-in-from-left-8 duration-700">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-3 bg-white/10 rounded-xl backdrop-blur-sm">
              <Ticket className="w-10 h-10 text-white" />
            </div>
            <h1 className="text-3xl lg:text-4xl font-bold text-white tracking-tight font-[Manrope]">
              Eunoia EduHub Voucher
            </h1>
          </div>
          <p className="text-blue-100 text-lg lg:text-xl max-w-md leading-relaxed">
            Platform manajemen voucher Eunoia EduHub.
          </p>
          <div className="mt-8 flex gap-4 text-blue-200 text-sm">
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" /> Generate Instan
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-4 h-4" /> Validasi Real-time
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Options */}
      <div className="lg:w-1/2 bg-slate-50 p-8 lg:p-12 flex items-center justify-center">
        <div className="w-full max-w-md space-y-6 animate-in fade-in slide-in-from-right-8 duration-700">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-slate-900 font-[Manrope]">Selamat Datang</h2>
            <p className="text-slate-500 mt-2">Pilih portal yang sesuai</p>
          </div>

          <button 
            data-testid="admin-portal-btn"
            onClick={() => navigate('/admin')}
            className="group w-full bg-white p-6 rounded-xl shadow-sm border border-slate-200 hover:shadow-lg hover:border-[#60A0FF] transition-all text-left"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="p-3 bg-[#EBF3FF] rounded-lg group-hover:bg-[#60A0FF] group-hover:text-white transition-colors">
                <ShieldCheck className="w-7 h-7" />
              </div>
              <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-[#60A0FF] group-hover:translate-x-1 transition-all" />
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-1 font-[Manrope]">Portal Admin</h3>
            <p className="text-slate-500 text-sm">Login untuk manajemen voucher bisnis Anda</p>
            <p className="text-xs text-[#60A0FF] mt-2 font-mono">/admin</p>
          </button>

          <button 
            data-testid="customer-portal-btn"
            onClick={() => navigate('/validate')}
            className="group w-full bg-white p-6 rounded-xl shadow-sm border border-slate-200 hover:shadow-lg hover:border-lime-500 transition-all text-left"
          >
            <div className="flex justify-between items-start mb-4">
              <div className="p-3 bg-lime-50 rounded-lg group-hover:bg-lime-600 group-hover:text-white transition-colors">
                <Users className="w-7 h-7" />
              </div>
              <ArrowRight className="w-5 h-5 text-slate-300 group-hover:text-lime-500 group-hover:translate-x-1 transition-all" />
            </div>
            <h3 className="text-xl font-bold text-slate-800 mb-1 font-[Manrope]">Validasi Voucher</h3>
            <p className="text-slate-500 text-sm">Klaim voucher yang Anda terima</p>
            <p className="text-xs text-lime-600 mt-2 font-mono">/validate</p>
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ ADMIN LOGIN PAGE ============
const AdminLogin = ({ onLogin }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/auth/login`, { email, password });
      toast.success(res.data.message);
      onLogin(res.data.admin);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Terjadi kesalahan';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md p-8 rounded-2xl shadow-lg border border-slate-200 animate-in fade-in zoom-in-95 duration-500">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-[#D6E7FF] text-[#60A0FF] rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 font-[Manrope]">
            Login Admin
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
              <input 
                data-testid="email-input"
                type="email" 
                required 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-[#60A0FF] outline-none transition-all" 
                placeholder="nama@email.com" 
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
              <input 
                data-testid="password-input"
                type="password" 
                required 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-[#60A0FF] outline-none transition-all" 
                placeholder="••••••••" 
              />
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg flex items-center gap-2">
              <XCircle className="w-4 h-4 flex-shrink-0" /> {error}
            </div>
          )}

          <button 
            data-testid="submit-btn"
            type="submit" 
            disabled={loading} 
            className="w-full py-3 bg-[#60A0FF] text-white font-semibold rounded-lg hover:bg-[#4B8FEE] flex justify-center items-center gap-2 transition-colors disabled:opacity-50"
          >
            {loading && <Loader2 className="animate-spin w-4 h-4" />}
            Masuk Dashboard
          </button>

          {/* Forgot Password Link */}
          <div className="text-center mt-4">
            <button
              type="button"
              onClick={() => navigate('/admin/forgot-password')}
              className="text-sm text-[#60A0FF] hover:text-[#4B8FEE] hover:underline"
            >
              Lupa Password?
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============ FORGOT PASSWORD PAGE ============
const ForgotPassword = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await axios.post(`${API}/auth/forgot-password`, { email });
      setSuccess(true);
      toast.success(res.data.message);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Terjadi kesalahan';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white w-full max-w-md p-8 rounded-2xl shadow-lg border border-slate-200 text-center">
          <div className="w-14 h-14 bg-[#D6E7FF] text-[#60A0FF] rounded-full flex items-center justify-center mx-auto mb-4">
            <Mail className="w-7 h-7" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-4">Cek Email Anda</h2>
          <p className="text-slate-600 mb-6">
            Jika email terdaftar, link untuk reset password telah dikirim. 
            Silakan cek inbox atau folder spam Anda.
          </p>
          <button
            onClick={() => navigate('/admin')}
            className="w-full py-3 bg-[#60A0FF] text-white font-semibold rounded-lg hover:bg-[#4B8FEE] transition-colors"
          >
            Kembali ke Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md p-8 rounded-2xl shadow-lg border border-slate-200 animate-in fade-in zoom-in-95 duration-500">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-amber-100 text-amber-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 font-[Manrope]">
            Lupa Password
          </h2>
          <p className="text-slate-500 text-sm mt-2">
            Masukkan email terdaftar untuk menerima link reset password
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
            <div className="relative">
              <Mail className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
              <input 
                data-testid="forgot-email-input"
                type="email" 
                required 
                value={email} 
                onChange={(e) => setEmail(e.target.value)} 
                className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-[#60A0FF] outline-none transition-all" 
                placeholder="nama@email.com" 
              />
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg flex items-center gap-2">
              <XCircle className="w-4 h-4 flex-shrink-0" /> {error}
            </div>
          )}

          <button 
            data-testid="submit-forgot-btn"
            type="submit" 
            disabled={loading} 
            className="w-full py-3 bg-[#60A0FF] text-white font-semibold rounded-lg hover:bg-[#4B8FEE] flex justify-center items-center gap-2 transition-colors disabled:opacity-50"
          >
            {loading && <Loader2 className="animate-spin w-4 h-4" />}
            Kirim Link Reset
          </button>

          <button
            type="button"
            onClick={() => navigate('/admin')}
            className="w-full py-3 text-slate-600 font-medium hover:text-slate-800 transition-colors"
          >
            Kembali ke Login
          </button>
        </form>
      </div>
    </div>
  );
};

// ============ RESET PASSWORD PAGE ============
const ResetPassword = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');
  
  // Get token from URL
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get('token');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password !== confirmPassword) {
      setError('Password tidak cocok');
      return;
    }
    
    if (password.length < 6) {
      setError('Password minimal 6 karakter');
      return;
    }
    
    setLoading(true);

    try {
      const res = await axios.post(`${API}/auth/reset-password`, { 
        token,
        newPassword: password 
      });
      
      setSuccess(true);
      toast.success(res.data.message);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Terjadi kesalahan';
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white w-full max-w-md p-8 rounded-2xl shadow-lg border border-slate-200 text-center">
          <div className="w-14 h-14 bg-red-100 text-red-600 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-7 h-7" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-4">Link Tidak Valid</h2>
          <p className="text-slate-600 mb-6">
            Link reset password tidak valid. Silakan request ulang.
          </p>
          <button
            onClick={() => navigate('/admin/forgot-password')}
            className="w-full py-3 bg-[#60A0FF] text-white font-semibold rounded-lg hover:bg-[#4B8FEE] transition-colors"
          >
            Request Link Baru
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
        <div className="bg-white w-full max-w-md p-8 rounded-2xl shadow-lg border border-slate-200 text-center">
          <div className="w-14 h-14 bg-[#D6E7FF] text-[#60A0FF] rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle className="w-7 h-7" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-4">Password Berhasil Diubah!</h2>
          <p className="text-slate-600 mb-6">
            Silakan login dengan password baru Anda.
          </p>
          <button
            onClick={() => navigate('/admin')}
            className="w-full py-3 bg-[#60A0FF] text-white font-semibold rounded-lg hover:bg-[#4B8FEE] transition-colors"
          >
            Login Sekarang
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center p-4">
      <div className="bg-white w-full max-w-md p-8 rounded-2xl shadow-lg border border-slate-200 animate-in fade-in zoom-in-95 duration-500">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-[#D6E7FF] text-[#60A0FF] rounded-full flex items-center justify-center mx-auto mb-4">
            <Lock className="w-7 h-7" />
          </div>
          <h2 className="text-2xl font-bold text-slate-800 font-[Manrope]">
            Buat Password Baru
          </h2>
          <p className="text-slate-500 text-sm mt-2">
            Masukkan password baru untuk akun Anda
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Password Baru</label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
              <input 
                data-testid="new-password-input"
                type="password" 
                required 
                value={password} 
                onChange={(e) => setPassword(e.target.value)} 
                className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-[#60A0FF] outline-none transition-all" 
                placeholder="Minimal 6 karakter" 
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">Konfirmasi Password</label>
            <div className="relative">
              <Lock className="w-4 h-4 absolute left-3 top-3 text-slate-400" />
              <input 
                data-testid="confirm-password-input"
                type="password" 
                required 
                value={confirmPassword} 
                onChange={(e) => setConfirmPassword(e.target.value)} 
                className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-[#60A0FF] outline-none transition-all" 
                placeholder="Ulangi password baru" 
              />
            </div>
          </div>

          {error && (
            <div className="p-3 bg-red-50 text-red-600 text-sm rounded-lg flex items-center gap-2">
              <XCircle className="w-4 h-4 flex-shrink-0" /> {error}
            </div>
          )}

          <button 
            data-testid="submit-reset-btn"
            type="submit" 
            disabled={loading} 
            className="w-full py-3 bg-[#60A0FF] text-white font-semibold rounded-lg hover:bg-[#4B8FEE] flex justify-center items-center gap-2 transition-colors disabled:opacity-50"
          >
            {loading && <Loader2 className="animate-spin w-4 h-4" />}
            Simpan Password Baru
          </button>
        </form>
      </div>
    </div>
  );
};

// ============ ADMIN DASHBOARD PAGE ============
const AdminDashboard = ({ admin, onLogout }) => {
  // Active menu state
  const [activeMenu, setActiveMenu] = useState('dashboard');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // Data states
  const [vouchers, setVouchers] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, redeemed: 0, clients: 0 });
  const [allAdmins, setAllAdmins] = useState([]);
  const [clientName, setClientName] = useState('');
  const [productName, setProductName] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [filterClient, setFilterClient] = useState('');
  const [lastBatch, setLastBatch] = useState([]);
  const [lastBatchClient, setLastBatchClient] = useState('');
  const [lastBatchProduct, setLastBatchProduct] = useState('');
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(true);
  
  // Report states
  const [reportClient, setReportClient] = useState('');
  const [clientList, setClientList] = useState([]);
  
  // Admin management states
  const [showAddAdmin, setShowAddAdmin] = useState(false);
  const [newAdminEmail, setNewAdminEmail] = useState('');
  const [newAdminPassword, setNewAdminPassword] = useState('');
  const [addingAdmin, setAddingAdmin] = useState(false);
  
  // Change password states
  const [showChangePassword, setShowChangePassword] = useState(false);
  const [selectedAdminForPassword, setSelectedAdminForPassword] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  const SUPER_ADMIN_EMAIL = "alfaruk.meka@gmail.com";
  const isSuperAdmin = admin?.email?.toLowerCase() === SUPER_ADMIN_EMAIL;

  const fetchData = useCallback(async () => {
    try {
      const [vRes, sRes] = await Promise.all([
        axios.get(`${API}/vouchers`, { params: { clientName: filterClient || undefined } }),
        axios.get(`${API}/stats`)
      ]);
      setVouchers(vRes.data.vouchers || []);
      setStats(sRes.data);
      
      // Get unique client names
      const clients = [...new Set(vRes.data.vouchers?.map(v => v.clientName) || [])];
      setClientList(clients);

      // Fetch all admins for admin management
      const aRes = await axios.get(`${API}/admin/all`);
      setAllAdmins(aRes.data.admins || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingData(false);
    }
  }, [filterClient]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handler functions
  const handleGenerate = async (e) => {
    e.preventDefault();
    if (!clientName.trim() || !productName.trim()) return;
    setLoading(true);

    try {
      const res = await axios.post(`${API}/vouchers/generate`, {
        clientName: clientName.trim(),
        productName: productName.trim(),
        quantity: Math.min(quantity, 100)
      }, { params: { generatedBy: admin?.email } });

      setLastBatch(res.data.codes || []);
      setLastBatchClient(res.data.clientName || clientName);
      setLastBatchProduct(res.data.productName || productName);
      toast.success(res.data.message);
      setClientName('');
      setProductName('');
      setQuantity(1);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal membuat voucher');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Hapus voucher ini?')) return;
    try {
      await axios.delete(`${API}/vouchers/${id}`);
      toast.success('Voucher dihapus');
      fetchData();
    } catch (err) {
      toast.error('Gagal menghapus');
    }
  };

  const handleBatchDelete = async () => {
    if (!filterClient) return;
    if (!window.confirm(`Hapus SEMUA voucher untuk "${filterClient}"?`)) return;
    
    try {
      const res = await axios.post(`${API}/vouchers/batch-delete`, { clientName: filterClient });
      toast.success(res.data.message);
      setFilterClient('');
      fetchData();
    } catch (err) {
      toast.error('Gagal menghapus batch');
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Disalin!');
  };

  const handleExportReport = async (type) => {
    if (!reportClient) {
      toast.error('Pilih klien terlebih dahulu');
      return;
    }
    
    const params = new URLSearchParams();
    params.append('clientName', reportClient);
    params.append('usedOnly', 'true');
    
    const url = `${API}/export/${type}?${params.toString()}`;
    window.open(url, '_blank');
    toast.success(`Mengunduh Report ${type.toUpperCase()}...`);
  };

  const handleAddAdmin = async (e) => {
    e.preventDefault();
    if (!newAdminEmail.trim() || !newAdminPassword.trim()) return;
    setAddingAdmin(true);

    try {
      const res = await axios.post(`${API}/admin/create`, {
        email: newAdminEmail.trim(),
        password: newAdminPassword
      });
      toast.success(res.data.message);
      setNewAdminEmail('');
      setNewAdminPassword('');
      setShowAddAdmin(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal menambah admin');
    } finally {
      setAddingAdmin(false);
    }
  };

  const handleDeleteAdmin = async (adminId, adminEmail) => {
    if (adminEmail === SUPER_ADMIN_EMAIL) {
      toast.error('Tidak dapat menghapus Super Admin');
      return;
    }
    if (adminEmail === admin?.email) {
      toast.error('Tidak dapat menghapus akun sendiri');
      return;
    }
    if (!window.confirm(`Hapus admin ${adminEmail}?`)) return;
    
    try {
      await axios.delete(`${API}/admin/${adminId}`);
      toast.success('Admin berhasil dihapus');
      fetchData();
    } catch (err) {
      toast.error('Gagal menghapus admin');
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!newPassword || !confirmNewPassword) return;
    if (newPassword !== confirmNewPassword) {
      toast.error('Password tidak cocok');
      return;
    }
    if (newPassword.length < 6) {
      toast.error('Password minimal 6 karakter');
      return;
    }
    
    setChangingPassword(true);
    try {
      await axios.put(`${API}/admin/${selectedAdminForPassword.id}/password`, {
        newPassword: newPassword
      });
      toast.success('Password berhasil diubah');
      setShowChangePassword(false);
      setSelectedAdminForPassword(null);
      setNewPassword('');
      setConfirmNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal mengubah password');
    } finally {
      setChangingPassword(false);
    }
  };

  const handleDownloadGenerated = async (type) => {
    if (lastBatch.length === 0) return;
    
    try {
      const response = await axios.post(
        `${API}/vouchers/export-generated/${type}`,
        {
          codes: lastBatch,
          clientName: lastBatchClient,
          productName: lastBatchProduct
        },
        { responseType: 'blob' }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const ext = type === 'excel' ? 'xlsx' : 'pdf';
      link.setAttribute('download', `Voucher_${lastBatchClient.replace(/\s/g, '_')}_${new Date().toISOString().slice(0,10)}.${ext}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${type.toUpperCase()} berhasil diunduh!`);
    } catch (err) {
      toast.error(`Gagal mengunduh ${type}`);
    }
  };

  // Menu items
  const menuItems = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutGrid },
    { id: 'generate', label: 'Generate Voucher', icon: Plus },
    { id: 'report', label: 'Report', icon: ClipboardList },
    { id: 'users', label: 'User Management', icon: Users },
  ];

  // Render Dashboard Content
  const renderDashboard = () => (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Total Voucher', value: stats.total, icon: Ticket, color: 'bg-slate-100 text-slate-600' },
          { label: 'Aktif', value: stats.active, icon: CheckCircle, color: 'bg-[#D6E7FF] text-[#60A0FF]' },
          { label: 'Terpakai', value: stats.redeemed, icon: UserCheck, color: 'bg-blue-100 text-blue-600' },
          { label: 'Klien', value: stats.clients, icon: Users, color: 'bg-purple-100 text-purple-600' },
        ].map((stat) => (
          <div key={stat.label} className="bg-white p-4 rounded-xl shadow-sm border border-slate-200">
            <div className="flex items-center gap-3">
              <div className={`p-2 rounded-lg ${stat.color}`}>
                <stat.icon className="w-5 h-5" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{stat.value}</p>
                <p className="text-xs text-slate-500">{stat.label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Voucher Table */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-200">
        <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row justify-between gap-3 bg-slate-50 rounded-t-xl">
          <div className="flex items-center gap-2">
            <History className="w-5 h-5 text-slate-600" />
            <h3 className="font-semibold text-slate-800 font-[Manrope]">Data Voucher</h3>
            <button onClick={fetchData} className="p-1 text-slate-400 hover:text-[#60A0FF] transition-colors">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
          
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative flex-1 min-w-[150px]">
              <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
              <input 
                type="text" 
                placeholder="Filter klien..." 
                value={filterClient} 
                onChange={(e) => setFilterClient(e.target.value)} 
                className="pl-9 pr-4 py-2 w-full rounded-lg border border-slate-300 text-sm focus:ring-2 focus:ring-[#60A0FF] outline-none"
              />
            </div>
            {filterClient && (
              <button onClick={handleBatchDelete} className="px-3 py-2 bg-red-600 text-white text-xs rounded-lg hover:bg-red-700">
                <Trash2 className="w-3 h-3 inline mr-1" /> Hapus Semua
              </button>
            )}
          </div>
        </div>
        
        <div className="overflow-auto max-h-[400px]">
          {loadingData ? (
            <div className="flex items-center justify-center h-40">
              <Loader2 className="w-8 h-8 animate-spin text-[#60A0FF]" />
            </div>
          ) : vouchers.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-slate-400">
              <Ticket className="w-12 h-12 mb-2" />
              <p>Belum ada voucher</p>
            </div>
          ) : (
            <table className="w-full text-left text-sm">
              <thead className="bg-white text-slate-500 sticky top-0 shadow-sm z-10">
                <tr>
                  <th className="px-3 py-3 border-b font-medium text-center w-12">No.</th>
                  <th className="px-4 py-3 border-b font-medium">Klien</th>
                  <th className="px-4 py-3 border-b font-medium">Produk</th>
                  <th className="px-4 py-3 border-b font-medium">Kode</th>
                  <th className="px-4 py-3 border-b font-medium">Status</th>
                  <th className="px-4 py-3 border-b font-medium">Penukar</th>
                  <th className="px-4 py-3 border-b font-medium text-right">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {vouchers.map(v => (
                  <tr key={v.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-3 py-3 text-center">
                      <span className="inline-flex items-center justify-center w-7 h-7 bg-slate-100 text-slate-700 rounded-full text-xs font-bold">
                        {v.sequenceNumber || '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 font-medium text-slate-800">{v.clientName}</td>
                    <td className="px-4 py-3 text-sm text-slate-600">{v.productName || '-'}</td>
                    <td className="px-4 py-3">
                      <code className="bg-slate-100 px-2 py-0.5 rounded text-xs font-mono">{v.code}</code>
                    </td>
                    <td className="px-4 py-3">
                      {v.isUsed ? (
                        <span className="text-xs bg-[#D6E7FF] text-[#3A7ED6] px-2 py-0.5 rounded-full font-medium">Terpakai</span>
                      ) : (
                        <span className="text-xs bg-green-100 text-green-800 px-2 py-0.5 rounded-full font-medium">Aktif</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-xs text-slate-500">
                      {v.isUsed && v.redeemedBy ? (
                        <div>
                          <p className="font-medium text-slate-700">{v.redeemedBy.name}</p>
                          <p>{v.redeemedBy.phone}</p>
                        </div>
                      ) : '-'}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button onClick={() => handleDelete(v.id)} className="text-slate-400 hover:text-red-600 transition-colors p-1">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );

  // Render Generate Voucher Content
  const renderGenerate = () => (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
        <h3 className="text-xl font-bold mb-6 flex items-center gap-2 pb-4 border-b border-slate-100 font-[Manrope]">
          <Plus className="w-6 h-6 text-[#60A0FF]" /> Generate Voucher Baru
        </h3>
        
        <form onSubmit={handleGenerate} className="space-y-5">
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Nama Klien/Bisnis</label>
            <input 
              data-testid="client-name-input"
              type="text" 
              required 
              value={clientName} 
              onChange={(e) => setClientName(e.target.value)} 
              placeholder="Contoh: PT. Maju Jaya" 
              className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-[#60A0FF] outline-none transition-all" 
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Nama Produk</label>
            <input 
              data-testid="product-name-input"
              type="text" 
              required 
              value={productName} 
              onChange={(e) => setProductName(e.target.value)} 
              placeholder="Contoh: Kursus Digital Marketing" 
              className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-[#60A0FF] outline-none transition-all" 
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">Jumlah Voucher (max 100)</label>
            <input 
              data-testid="quantity-input"
              type="number" 
              min="1" 
              max="100" 
              required 
              value={quantity} 
              onChange={(e) => setQuantity(parseInt(e.target.value) || 1)} 
              className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-[#60A0FF] outline-none transition-all" 
            />
          </div>
          
          <button 
            data-testid="generate-btn"
            type="submit" 
            disabled={loading} 
            className="w-full py-3 bg-[#60A0FF] text-white rounded-lg hover:bg-[#4B8FEE] font-medium flex justify-center gap-2 transition-colors disabled:opacity-50"
          >
            {loading ? <Loader2 className="animate-spin w-5 h-5" /> : <Plus className="w-5 h-5" />}
            Generate Voucher
          </button>
        </form>

        {/* Generated Codes Result */}
        {lastBatch.length > 0 && (
          <div className="mt-6 p-4 bg-slate-50 rounded-lg border border-slate-200">
            <div className="flex justify-between items-center mb-3">
              <div>
                <span className="text-sm font-bold text-slate-700">Hasil Generate ({lastBatch.length} kode)</span>
                <p className="text-sm text-slate-600">{lastBatchClient}</p>
                <p className="text-sm text-[#60A0FF]">{lastBatchProduct}</p>
              </div>
              <button 
                onClick={() => copyToClipboard(lastBatch.join('\n'))} 
                className="text-sm text-[#60A0FF] font-bold flex gap-1 hover:underline"
              >
                <Copy className="w-4 h-4" /> Salin Semua
              </button>
            </div>
            
            <div className="max-h-40 overflow-y-auto font-mono text-sm text-slate-600 space-y-1 mb-4 bg-white p-3 rounded-lg">
              {lastBatch.map((c, i) => (
                <div key={i} className="flex justify-between items-center px-2 py-1 hover:bg-slate-50 rounded">
                  <span>{c}</span>
                  <button onClick={() => copyToClipboard(c)} className="text-slate-400 hover:text-[#60A0FF]">
                    <Copy className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
            
            <div className="flex gap-3">
              <button 
                onClick={() => handleDownloadGenerated('excel')}
                className="flex-1 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 transition-colors"
              >
                <FileSpreadsheet className="w-4 h-4" /> Download Excel
              </button>
              <button 
                onClick={() => handleDownloadGenerated('pdf')}
                className="flex-1 py-2.5 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center gap-2 transition-colors"
              >
                <FileText className="w-4 h-4" /> Download PDF
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Render Report Content
  const renderReport = () => {
    const redeemedVouchers = vouchers.filter(v => v.isUsed && (!reportClient || v.clientName === reportClient));
    
    return (
      <div className="space-y-6">
        <div className="bg-white p-6 rounded-xl shadow-sm border border-slate-200">
          <h3 className="text-xl font-bold mb-6 flex items-center gap-2 pb-4 border-b border-slate-100 font-[Manrope]">
            <ClipboardList className="w-6 h-6 text-[#60A0FF]" /> Report Voucher Diklaim
          </h3>
          
          <div className="grid md:grid-cols-2 gap-4 mb-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Pilih Klien</label>
              <select
                value={reportClient}
                onChange={(e) => setReportClient(e.target.value)}
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-[#60A0FF] outline-none"
              >
                <option value="">-- Semua Klien --</option>
                {clientList.map((client) => (
                  <option key={client} value={client}>{client}</option>
                ))}
              </select>
            </div>
            
            <div className="flex items-end gap-3">
              <button 
                onClick={() => handleExportReport('excel')}
                disabled={!reportClient}
                className="flex-1 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FileSpreadsheet className="w-5 h-5" /> Export Excel
              </button>
              <button 
                onClick={() => handleExportReport('pdf')}
                disabled={!reportClient}
                className="flex-1 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <FileText className="w-5 h-5" /> Export PDF
              </button>
            </div>
          </div>
          
          {/* Preview Table */}
          <div className="border border-slate-200 rounded-lg overflow-hidden">
            <div className="bg-slate-50 px-4 py-3 border-b border-slate-200">
              <h4 className="font-semibold text-slate-700">
                Preview: Voucher Diklaim {reportClient ? `(${reportClient})` : '(Semua Klien)'} - {redeemedVouchers.length} data
              </h4>
            </div>
            
            <div className="overflow-auto max-h-[350px]">
              {redeemedVouchers.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-40 text-slate-400">
                  <ClipboardList className="w-12 h-12 mb-2" />
                  <p>Belum ada voucher diklaim</p>
                </div>
              ) : (
                <table className="w-full text-left text-sm">
                  <thead className="bg-white text-slate-500 sticky top-0 shadow-sm">
                    <tr>
                      <th className="px-4 py-3 border-b font-medium">No</th>
                      <th className="px-4 py-3 border-b font-medium">Klien</th>
                      <th className="px-4 py-3 border-b font-medium">Produk</th>
                      <th className="px-4 py-3 border-b font-medium">Kode</th>
                      <th className="px-4 py-3 border-b font-medium">Nama Penukar</th>
                      <th className="px-4 py-3 border-b font-medium">HP</th>
                      <th className="px-4 py-3 border-b font-medium">Email</th>
                      <th className="px-4 py-3 border-b font-medium">Tanggal Klaim</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {redeemedVouchers.map((v, idx) => (
                      <tr key={v.id} className="hover:bg-slate-50">
                        <td className="px-4 py-3">{idx + 1}</td>
                        <td className="px-4 py-3 font-medium">{v.clientName}</td>
                        <td className="px-4 py-3">{v.productName || '-'}</td>
                        <td className="px-4 py-3"><code className="bg-slate-100 px-2 py-0.5 rounded text-xs">{v.code}</code></td>
                        <td className="px-4 py-3">{v.redeemedBy?.name || '-'}</td>
                        <td className="px-4 py-3">{v.redeemedBy?.phone || '-'}</td>
                        <td className="px-4 py-3">{v.redeemedBy?.email || '-'}</td>
                        <td className="px-4 py-3 text-xs">{v.redeemedAt ? new Date(v.redeemedAt).toLocaleString('id-ID') : '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // Render User Management Content
  const renderUsers = () => (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
        <div className="bg-slate-50 px-6 py-4 border-b border-slate-100 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Users className="w-6 h-6 text-[#60A0FF]" />
            <h3 className="text-xl font-bold text-slate-800 font-[Manrope]">User Management</h3>
          </div>
          {isSuperAdmin && (
            <button 
              onClick={() => setShowAddAdmin(!showAddAdmin)}
              className="px-4 py-2 bg-[#60A0FF] text-white rounded-lg hover:bg-[#4B8FEE] flex items-center gap-2 transition-colors"
            >
              <Plus className="w-4 h-4" /> Tambah Admin
            </button>
          )}
        </div>

        {/* Add Admin Form */}
        {showAddAdmin && isSuperAdmin && (
          <div className="p-6 bg-blue-50 border-b border-blue-100">
            <h4 className="font-semibold text-slate-700 mb-4">Tambah Admin Baru</h4>
            <form onSubmit={handleAddAdmin} className="space-y-4">
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email</label>
                  <input 
                    data-testid="new-admin-email"
                    type="email" 
                    required 
                    value={newAdminEmail} 
                    onChange={(e) => setNewAdminEmail(e.target.value)} 
                    placeholder="admin@email.com" 
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-[#60A0FF] outline-none" 
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Password</label>
                  <input 
                    data-testid="new-admin-password"
                    type="password" 
                    required 
                    value={newAdminPassword} 
                    onChange={(e) => setNewAdminPassword(e.target.value)} 
                    placeholder="Min. 6 karakter" 
                    className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-[#60A0FF] outline-none" 
                  />
                </div>
              </div>
              <div className="flex gap-3">
                <button 
                  type="button" 
                  onClick={() => { setShowAddAdmin(false); setNewAdminEmail(''); setNewAdminPassword(''); }}
                  className="px-4 py-2 border border-slate-300 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  Batal
                </button>
                <button 
                  data-testid="submit-new-admin"
                  type="submit" 
                  disabled={addingAdmin}
                  className="px-6 py-2 bg-[#60A0FF] text-white rounded-lg hover:bg-[#4B8FEE] flex items-center gap-2 transition-colors disabled:opacity-50"
                >
                  {addingAdmin && <Loader2 className="w-4 h-4 animate-spin" />}
                  Tambah Admin
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Admin List */}
        <div className="divide-y divide-slate-100">
          {allAdmins.length === 0 ? (
            <div className="p-10 text-center text-slate-400">
              <Users className="w-12 h-12 mx-auto mb-2" />
              <p>Belum ada admin</p>
            </div>
          ) : (
            allAdmins.map((a) => (
              <div key={a.id} className="p-4 flex items-center justify-between hover:bg-slate-50">
                <div className="flex items-center gap-4">
                  <div className="w-10 h-10 bg-[#D6E7FF] rounded-full flex items-center justify-center">
                    <UserCheck className="w-5 h-5 text-[#60A0FF]" />
                  </div>
                  <div>
                    <p className="font-medium text-slate-800">{a.email}</p>
                    <p className="text-xs text-slate-500">
                      {a.email === SUPER_ADMIN_EMAIL ? (
                        <span className="text-amber-600 font-medium">Super Admin</span>
                      ) : (
                        'Admin'
                      )}
                      {' • '}
                      <span>Dibuat: {new Date(a.createdAt).toLocaleDateString('id-ID')}</span>
                    </p>
                  </div>
                </div>
                
                <div className="flex items-center gap-2">
                  {/* Change Password Button */}
                  {(isSuperAdmin || a.email === admin?.email) && (
                    <button
                      onClick={() => { setSelectedAdminForPassword(a); setShowChangePassword(true); }}
                      className="p-2 text-slate-500 hover:text-[#60A0FF] hover:bg-[#D6E7FF] rounded-lg transition-colors"
                      title="Ganti Password"
                    >
                      <Key className="w-4 h-4" />
                    </button>
                  )}
                  
                  {/* Delete Button */}
                  {isSuperAdmin && a.email !== SUPER_ADMIN_EMAIL && a.email !== admin?.email && (
                    <button
                      onClick={() => handleDeleteAdmin(a.id, a.email)}
                      className="p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title="Hapus Admin"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Change Password Modal */}
      {showChangePassword && selectedAdminForPassword && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6 animate-in fade-in zoom-in-95">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-slate-800">Ganti Password</h3>
              <button onClick={() => { setShowChangePassword(false); setSelectedAdminForPassword(null); setNewPassword(''); setConfirmNewPassword(''); }} className="text-slate-400 hover:text-slate-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <p className="text-sm text-slate-600 mb-4">
              Ganti password untuk: <span className="font-medium">{selectedAdminForPassword.email}</span>
            </p>
            
            <form onSubmit={handleChangePassword} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Password Baru</label>
                <input 
                  type="password" 
                  required 
                  value={newPassword} 
                  onChange={(e) => setNewPassword(e.target.value)} 
                  placeholder="Min. 6 karakter" 
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-[#60A0FF] outline-none" 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Konfirmasi Password</label>
                <input 
                  type="password" 
                  required 
                  value={confirmNewPassword} 
                  onChange={(e) => setConfirmNewPassword(e.target.value)} 
                  placeholder="Ulangi password baru" 
                  className="w-full px-4 py-2.5 rounded-lg border border-slate-300 focus:ring-2 focus:ring-[#60A0FF] outline-none" 
                />
              </div>
              <div className="flex gap-3 pt-2">
                <button 
                  type="button" 
                  onClick={() => { setShowChangePassword(false); setSelectedAdminForPassword(null); setNewPassword(''); setConfirmNewPassword(''); }}
                  className="flex-1 px-4 py-2.5 border border-slate-300 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  Batal
                </button>
                <button 
                  type="submit" 
                  disabled={changingPassword}
                  className="flex-1 px-4 py-2.5 bg-[#60A0FF] text-white rounded-lg hover:bg-[#4B8FEE] flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                >
                  {changingPassword && <Loader2 className="w-4 h-4 animate-spin" />}
                  Simpan
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );

  // Main render
  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-40 w-64 bg-white shadow-lg transform transition-transform duration-300 lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-5 border-b border-slate-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-[#60A0FF] rounded-lg">
                <Ticket className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="font-bold text-slate-800 font-[Manrope]">Eunoia EduHub</h1>
                <p className="text-xs text-slate-500">Voucher System</p>
              </div>
            </div>
          </div>
          
          {/* Menu Items */}
          <nav className="flex-1 p-4 space-y-1">
            {menuItems.map((item) => (
              <button
                key={item.id}
                onClick={() => { setActiveMenu(item.id); setSidebarOpen(false); }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                  activeMenu === item.id 
                    ? 'bg-[#60A0FF] text-white' 
                    : 'text-slate-600 hover:bg-slate-100'
                }`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </button>
            ))}
          </nav>
          
          {/* User Info & Logout */}
          <div className="p-4 border-t border-slate-100">
            <div className="mb-3 px-4">
              <p className="text-sm font-medium text-slate-800 truncate">{admin?.email}</p>
              <p className="text-xs text-slate-500">{isSuperAdmin ? 'Super Admin' : 'Admin'}</p>
            </div>
            <button 
              onClick={onLogout}
              className="w-full flex items-center gap-3 px-4 py-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Logout</span>
            </button>
          </div>
        </div>
      </aside>
      
      {/* Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-30 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
      
      {/* Main Content */}
      <div className="flex-1 lg:ml-64">
        {/* Top Header */}
        <header className="bg-white shadow-sm border-b border-slate-200 sticky top-0 z-20">
          <div className="px-4 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button 
                onClick={() => setSidebarOpen(true)}
                className="lg:hidden p-2 text-slate-600 hover:bg-slate-100 rounded-lg"
              >
                <Menu className="w-5 h-5" />
              </button>
              <h2 className="text-lg font-bold text-slate-800 font-[Manrope]">
                {menuItems.find(m => m.id === activeMenu)?.label || 'Dashboard'}
              </h2>
            </div>
            <button onClick={fetchData} className="p-2 text-slate-400 hover:text-[#60A0FF] hover:bg-slate-100 rounded-lg transition-colors">
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </header>
        
        {/* Page Content */}
        <main className="p-6">
          {activeMenu === 'dashboard' && renderDashboard()}
          {activeMenu === 'generate' && renderGenerate()}
          {activeMenu === 'report' && renderReport()}
          {activeMenu === 'users' && renderUsers()}
        </main>
      </div>
    </div>
  );
};

// ============ CUSTOMER VALIDATION PAGE ============
const CustomerValidation = () => {
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [status, setStatus] = useState(null);
  const [voucher, setVoucher] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '' });
  const [loading, setLoading] = useState(false);

  const checkVoucher = async () => {
    if (!code.trim()) return;
    setStatus('checking');

    try {
      const res = await axios.post(`${API}/vouchers/validate`, { code: code.trim() });
      setStatus(res.data.status);
      setVoucher(res.data.voucher || null);
      
      if (res.data.status === 'invalid') {
        toast.error('Kode tidak ditemukan');
      } else if (res.data.status === 'used') {
        toast.warning('Voucher sudah terpakai');
      } else {
        toast.success('Kode Valid!');
      }
    } catch (err) {
      setStatus('invalid');
      toast.error(err.response?.data?.detail || 'Gagal memvalidasi kode');
    }
  };

  const handleRedeem = async (e) => {
    e.preventDefault();
    if (!voucher) return;
    setLoading(true);

    try {
      await axios.post(`${API}/vouchers/redeem`, {
        code: code.trim(),
        name: form.name,
        phone: form.phone,
        email: form.email
      });
      
      setStatus('success');
      toast.success('Voucher berhasil diklaim!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Gagal mengklaim voucher');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setCode('');
    setStatus(null);
    setVoucher(null);
    setForm({ name: '', phone: '', email: '' });
  };

  return (
    <div className="min-h-screen bg-slate-100 flex flex-col">
      {/* Header */}
      <header className="bg-white shadow-sm py-4 px-6 flex justify-between items-center">
        <div className="flex items-center gap-2 font-bold text-slate-800 text-lg font-[Manrope]">
          <Ticket className="w-6 h-6 text-[#60A0FF]" /> Validasi Voucher
        </div>
      </header>

      <main className="flex-1 flex items-center justify-center p-4">
        <div className="bg-white w-full max-w-lg rounded-2xl shadow-lg border border-slate-200 overflow-hidden animate-in fade-in zoom-in-95 duration-500">
          {/* Hero */}
          <div className="bg-gradient-to-br from-[#60A0FF] to-[#4B8FEE] p-8 text-center text-white">
            <CheckCircle className="w-12 h-12 mx-auto mb-3 opacity-90" />
            <h2 className="text-2xl font-bold font-[Manrope]">Klaim Voucher Anda</h2>
            <p className="text-blue-100 text-sm mt-1">Masukkan kode unik yang Anda terima</p>
          </div>

          <div className="p-8">
            {/* Input State */}
            {(!status || status === 'invalid' || status === 'used') && status !== 'valid' && status !== 'success' && (
              <div className="space-y-4">
                <div>
                  <input 
                    data-testid="voucher-code-input"
                    type="text" 
                    value={code} 
                    onChange={(e) => { setCode(e.target.value); if (status) setStatus(null); }}
                    placeholder="Masukkan Kode Di Sini"
                    className="w-full text-center text-xl tracking-wider font-mono px-4 py-4 rounded-xl border-2 border-slate-300 focus:border-[#60A0FF] focus:ring-2 focus:ring-[#A8CDFF] outline-none transition-all"
                  />
                  <p className="text-xs text-slate-500 mt-2 text-center">
                    * Kode bersifat case-sensitive (huruf besar & kecil berbeda)
                  </p>
                </div>
                
                <button 
                  data-testid="check-code-btn"
                  onClick={checkVoucher} 
                  disabled={!code.trim() || status === 'checking'} 
                  className="w-full py-3 bg-slate-900 text-white rounded-xl hover:bg-slate-800 transition-colors font-bold disabled:opacity-50 flex items-center justify-center gap-2"
                >
                  {status === 'checking' && <Loader2 className="w-4 h-4 animate-spin" />}
                  CEK KODE
                </button>
                
                {status === 'invalid' && (
                  <div className="text-center text-red-600 bg-red-50 p-3 rounded-lg text-sm flex items-center justify-center gap-2">
                    <XCircle className="w-4 h-4" /> Kode tidak ditemukan
                  </div>
                )}
                {status === 'used' && voucher && (
                  <div className="space-y-3">
                    <div className="text-center text-amber-600 bg-amber-50 p-3 rounded-lg text-sm flex items-center justify-center gap-2">
                      <AlertCircle className="w-4 h-4" /> Voucher sudah terpakai
                    </div>
                    <div className="bg-slate-50 p-4 rounded-lg text-sm space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-slate-500">Bisnis:</span>
                        <span className="font-semibold text-slate-800">{voucher.clientName}</span>
                      </div>
                      {voucher.productName && (
                        <div className="flex justify-between items-center">
                          <span className="text-slate-500">Produk:</span>
                          <span className="font-semibold text-[#60A0FF]">{voucher.productName}</span>
                        </div>
                      )}
                      {voucher.redeemedBy && (
                        <div className="pt-2 mt-2 border-t border-slate-200">
                          <p className="text-xs text-slate-400">Diklaim oleh: {voucher.redeemedBy.name}</p>
                        </div>
                      )}
                    </div>
                  </div>
                )}
                {status === 'used' && !voucher && (
                  <div className="text-center text-amber-600 bg-amber-50 p-3 rounded-lg text-sm flex items-center justify-center gap-2">
                    <AlertCircle className="w-4 h-4" /> Voucher sudah terpakai
                  </div>
                )}
              </div>
            )}

            {/* Valid - Show Form */}
            {status === 'valid' && (
              <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
                <div className="bg-[#EBF3FF] text-[#3A7ED6] p-3 rounded-lg mb-6 text-sm flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" /> Kode Valid! Lengkapi data untuk klaim.
                </div>
                
                {voucher && (
                  <div className="bg-slate-50 p-4 rounded-lg mb-4 text-sm space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-slate-500">Bisnis:</span>
                      <span className="font-semibold text-slate-800">{voucher.clientName}</span>
                    </div>
                    {voucher.productName && (
                      <div className="flex justify-between items-center">
                        <span className="text-slate-500">Produk:</span>
                        <span className="font-semibold text-[#60A0FF]">{voucher.productName}</span>
                      </div>
                    )}
                  </div>
                )}

                <form onSubmit={handleRedeem} className="space-y-4">
                  <input 
                    data-testid="redeem-name-input"
                    required 
                    type="text" 
                    placeholder="Nama Lengkap" 
                    value={form.name} 
                    onChange={e => setForm({...form, name: e.target.value})} 
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#60A0FF] outline-none transition-all" 
                  />
                  <div className="grid grid-cols-2 gap-4">
                    <input 
                      data-testid="redeem-phone-input"
                      required 
                      type="tel" 
                      placeholder="No. HP" 
                      value={form.phone} 
                      onChange={e => setForm({...form, phone: e.target.value})} 
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#60A0FF] outline-none transition-all" 
                    />
                    <input 
                      data-testid="redeem-email-input"
                      required 
                      type="email" 
                      placeholder="Email" 
                      value={form.email} 
                      onChange={e => setForm({...form, email: e.target.value})} 
                      className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-[#60A0FF] outline-none transition-all" 
                    />
                  </div>
                  <div className="flex gap-3 pt-2">
                    <button 
                      data-testid="cancel-redeem-btn"
                      type="button" 
                      onClick={reset} 
                      className="flex-1 py-3 border border-slate-300 rounded-lg text-slate-600 hover:bg-slate-50 transition-colors"
                    >
                      Batal
                    </button>
                    <button 
                      data-testid="submit-redeem-btn"
                      type="submit" 
                      disabled={loading}
                      className="flex-1 py-3 bg-[#60A0FF] text-white rounded-lg font-bold hover:bg-[#4B8FEE] transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
                    >
                      {loading && <Loader2 className="w-4 h-4 animate-spin" />}
                      Klaim Voucher
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Success State */}
            {status === 'success' && (
              <div className="text-center py-6 animate-in fade-in zoom-in duration-500">
                <div className="w-16 h-16 bg-[#D6E7FF] text-[#60A0FF] rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-8 h-8" />
                </div>
                <h3 className="text-[#60A0FF] font-bold text-xl mb-2 font-[Manrope]">Berhasil!</h3>
                <p className="text-slate-500 mb-6">Data Anda telah disimpan. Cek email untuk konfirmasi.</p>
                <button 
                  data-testid="new-code-btn"
                  onClick={reset} 
                  className="text-[#60A0FF] font-medium hover:underline"
                >
                  Input Kode Lain
                </button>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

// ============ ADMIN ROUTES WRAPPER ============
const AdminRoutes = ({ admin, setAdmin }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleLogin = (adminData) => {
    setAdmin(adminData);
    navigate('/admin/dashboard');
  };

  const handleLogout = () => {
    setAdmin(null);
    navigate('/');
  };

  // Redirect logic
  useEffect(() => {
    if (location.pathname === '/admin' || location.pathname === '/admin/') {
      if (admin) {
        navigate('/admin/dashboard');
      }
    }
  }, [admin, location.pathname, navigate]);

  return (
    <Routes>
      <Route index element={<AdminLogin onLogin={handleLogin} />} />
      <Route path="forgot-password" element={<ForgotPassword />} />
      <Route path="reset-password" element={<ResetPassword />} />
      <Route path="dashboard" element={
        admin ? (
          <AdminDashboard admin={admin} onLogout={handleLogout} />
        ) : (
          <AdminLogin onLogin={handleLogin} />
        )
      } />
    </Routes>
  );
};

// ============ MAIN APP ============
function App() {
  const [admin, setAdmin] = useState(null);
  
  // Remove Emergent badge
  useRemoveBadge();

  return (
    <BrowserRouter>
      <div className="App">
        <Toaster position="top-right" richColors />
        
        <Routes>
          <Route path="/" element={<Portal />} />
          <Route path="/admin/*" element={<AdminRoutes admin={admin} setAdmin={setAdmin} />} />
          <Route path="/validate" element={<CustomerValidation />} />
        </Routes>
      </div>
    </BrowserRouter>
  );
}

export default App;
